<!-- src/pages/archive/ControlDesc_2_0_A_P.vue -->
<template>
  <main class="archive-tree">

    <section class="desc">
      <h1 class="title">유형별 통제매트릭스 사전</h1>
      <p>
        본 자료는 국제내부통제협회(Internal Control Institute)에서 제공하는 내부통제 지식 프레임워크로, 조직의 내부통제를 진단하고 개선하는 실무 도구입니다.<br /> 조직의 특성과
        리스크 수준에
        맞게 선택·적용·개선함으로써 내부통제의 성숙도를 지속적으로 향상시킬 수 있습니다.
      </p>
      <p>
        내부통제와 관련된 지식과 기술 요소를 백과사전식으로 체계화하여, 조직이 내부통제 체계를 설계하거나 개선할 때 참고할 수 있는 통제 활동, 절차, 기준 및 실행 가이드라인을 제공합니다.
      </p>
      <p>
        이 자료는 경영진 또는 감사·리스크 관리·내부통제 담당자가 통제 미비 영역을 식별했을 때, 적절한 보완 통제수단을 선정하고 적용하는 데 유용하게 활용될 수 있습니다.
      </p>

      <h2 class="usage-guide-wrap">사용방법</h2>

      <section class="usage-guide">
        <ol>
          <li>
            <strong>① 통제 대상 프로세스 식별</strong><br />
            영역별 분류(절차, 기준, 감사추적, 책임분리 등), 통제유형별 분류(예방적·탐지적·시정적) 등을 활용해 관련 통제 항목을 찾습니다.
          </li>

          <li>
            <strong>② 관련 통제 항목 탐색</strong><br />
            예시<br />
            통제명: 접근권한 검토(Control Review of Access Rights)<br />
            유형: 탐지적 통제<br />
            목적: 불필요한 시스템 접근 권한의 지속 사용 방지<br />
            실행 지침: 분기별 권한 검토 및 관리자 승인 절차<br />
            <em>*각 항목에는 통제 목적, 수행 절차, 적용 예시, 주의사항 등이 함께 제시됩니다.</em>
          </li>

          <li>
            <strong>③ 현재 운영 중인 통제와 비교</strong><br />
            백과사전의 표준 통제 절차와 조직의 현재 운영 중인 내부통제 현황을 비교하여 누락되었거나 비효율적인 부분을 식별합니다.<br />
            갭 분석 결과를 바탕으로, 통제의 “Implementation Guide” 섹션을 참조해 적절한 통제활동을 구체적으로 설계·도입합니다.<br />
            본 백과사전의 모든 통제를 일괄 적용할 필요는 없으며, 조직의 규모·위험 수준·운영 특성에 따라 선택적으로 활용할 수 있습니다.
          </li>
        </ol>
      </section>

    </section>

  <!-- selector section -->
  <section class="selector" aria-label="코드 선택">
    <div class="grid">
      <!-- Generic Category -->
      <div class="group">
        <h3>Generic Category</h3>
        <label
          v-for="g in categories.generic"
          :key="g.code"
          :class="{ disabled: false }"
        >
          <input
            type="radio"
            name="generic"
            :value="g.code"
            v-model="selected.generic"
            @change="onPickGeneric"
          />
          <span class="code">{{ g.code }}</span>
          <span class="label">{{ g.label }}</span>
        </label>
      </div>

      <!-- Usage Activity (generic 선택 결과에 따라 활성/비활성) -->
      <div class="group">
        <h3>Usage Activity</h3>
        <label
          v-for="u in categories.usage"
          :key="u.code"
          :class="{ disabled: !available.usage.has(u.code) }"
        >
          <input
            type="radio"
            name="usage"
            :value="u.code"
            v-model="selected.usage"
            :disabled="!available.usage.has(u.code)"
            @change="onPickUsage"
          />
          <span class="code">{{ u.code }}</span>
          <span class="label">{{ u.label }}</span>
        </label>
      </div>

      <!-- Strength of Control (usage 선택 결과에 따라 활성/비활성) -->
      <div class="group">
        <h3>Strength of Control</h3>
        <label
          v-for="s in categories.strength"
          :key="s.code"
          :class="{ disabled: !available.strength.has(s.code) }"
        >
          <input
            type="radio"
            name="strength"
            :value="s.code"
            v-model="selected.strength"
            :disabled="!available.strength.has(s.code)"
            @change="onPickStrength"
          />
          <span class="code">{{ s.code }}</span>
          <span class="label">{{ s.label }}</span>
        </label>
      </div>

      <!-- Control Type (strength 선택 결과에 따라 활성/비활성) -->
      <div class="group">
        <h3>Control Type</h3>
        <label
          v-for="t in categories.type"
          :key="t.code"
          :class="{ disabled: !available.type.has(t.code) }"
        >
          <input
            type="radio"
            name="type"
            :value="t.code"
            v-model="selected.type"
            :disabled="!available.type.has(t.code)"
          />
          <span class="code">{{ t.code }}</span>
          <span class="label">{{ t.label }}</span>
        </label>
      </div>
    </div>

    <!-- 조회 -->
    <div class="search-fixed">
      <button type="button" class="btn-search" @click="onSearch">조회하기</button>
      <p v-if="hint" class="hint">{{ hint }}</p>
    </div>
  </section>

  <!-- documents section (조회 전 숨김) -->
  <section
    v-if="showDoc && doc"
    class="control-sheet"
    aria-labelledby="doc-title"
  >
    <h1 id="doc-title" class="title1">CONTROL DESCRIPTION</h1>

    <section class="sheet-grid" role="table" aria-label="Control description detail">
      <!-- Ref.# + NAME OF CONTROL -->
      <div class="row row--head" role="row">
        <div class="th" role="columnheader">Ref.#</div>
        <div class="td" role="cell"><p class="ref-code">{{ doc.referenceCode }}</p></div>
        <div class="th" role="columnheader">NAME OF CONTROL</div>
        <div class="td" role="cell"><p class="name">{{ doc.nameOfControl }}</p></div>
      </div>

      <!-- META -->
      <div class="row row--meta" role="row">
        <div class="th" role="columnheader">GENERIC CATEGORY DESCRIPTION</div>
        <div class="td" role="cell"><p>{{ doc.genericCategory }}</p></div>

        <div class="th" role="columnheader">USAGE ACTIVITY</div>
        <div class="td" role="cell"><p>{{ doc.usageActivity }}</p></div>

        <div class="th" role="columnheader">STRENGTH</div>
        <div class="td" role="cell"><p>{{ doc.strength }}</p></div>

        <div class="th" role="columnheader">CONTROL TYPE</div>
        <div class="td" role="cell"><p>{{ doc.controlType }}</p></div>
      </div>

      <!-- DESCRIPTION -->
      <div class="row" role="row">
        <div class="th" role="columnheader">DESCRIPTION</div>
        <div class="td" role="cell"><p>{{ doc.description }}</p></div>
      </div>

      <!-- IMPLEMENTATION SUGGESTIONS -->
      <div class="row" role="row">
        <div class="th" role="columnheader">IMPLEMENTATION SUGGESTIONS</div>
        <div class="td" role="cell"><p>{{ doc.implementationSuggestions }}</p></div>
      </div>

      <!-- FEEDBACK EVIDENCE -->
      <div class="row" role="row">
        <div class="th" role="columnheader">FEEDBACK EVIDENCE</div>
        <div class="td" role="cell"><p>{{ doc.feedbackEvidence }}</p></div>
      </div>

      <!-- ADVANTAGES OF CONTROL -->
      <div class="row" role="row">
        <div class="th" role="columnheader">ADVANTAGES OF CONTROL</div>
        <div class="td" role="cell"><p>{{ doc.advantages }}</p></div>
      </div>

      <!-- DISADVANTAGES OF CONTROL -->
      <div class="row" role="row">
        <div class="th" role="columnheader">DISADVANTAGES OF CONTROL</div>
        <div class="td" role="cell"><p>{{ doc.disadvantages }}</p></div>
      </div>
    </section>
  </section>
  </main>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { selectLibrary, referencechildren } from '@/api/room'
import { useStore } from 'vuex'

const checkUserId = computed(() => store.state.temp.userInfo)
const store = useStore()


/** ===== 카테고리(고정) ===== */
const categories = reactive({
  generic: [
    { code: '1', label: 'Access' },
    { code: '2', label: 'Authorization' },
    { code: '3', label: 'Containment' },
    { code: '4', label: 'Limit' },
    { code: '5', label: 'Procedures' },
    { code: '6', label: 'Segregate' },
    { code: '7', label: 'Standards' },
    { code: '8', label: 'Void' },
    { code: '9', label: 'Accounting' },
    { code: '10', label: 'Confirm' },
    { code: '11', label: 'Compliance' },
    { code: '12', label: 'Identify' },
    { code: '13', label: 'Simulate' },
    { code: '14', label: 'Observe' },
    { code: '15', label: 'Redundancy' },
    { code: '16', label: 'Feedback' },
    { code: '17', label: 'Sample' },
    { code: '18', label: 'Tag' },
    { code: '19', label: 'Verify' },
    { code: '20', label: 'Scan' },
    { code: '21', label: 'Copy' },
    { code: '22', label: 'Defaults' },
    { code: '23', label: 'Prompt' },
    { code: '24', label: 'Retrain' },
  ],
  usage: [
    { code: '1', label: 'Origination' },
    { code: '2', label: 'Entry' },
    { code: '3', label: 'Communications' },
    { code: '4', label: 'Process' },
    { code: '5', label: 'Storage (on-line)' },
    { code: '6', label: 'Storage (off-line)' },
    { code: '7', label: 'Output' },
    { code: '8', label: 'Use of Data' },
    { code: '9', label: 'General Purpose' },
    { code: '0', label: 'Authorization' },
  ],
  strength: [
    { code: 'A', label: 'Very Strong' },
    { code: 'B', label: 'Strong' },
    { code: 'C', label: 'Average' },
    { code: 'D', label: 'Minimal' },
  ],
  type: [
    { code: 'P', label: 'Preventive' },
    { code: 'D', label: 'Detective' },
    { code: 'C', label: 'Corrective' },
  ],
})

/** ===== 선택 상태 ===== */
const selected = reactive({ generic: '', usage: '', strength: '', type: '' })
const showDoc = ref(false)
const hint = ref('')
const doc = ref(null)

/** ===== 단계별 활성 가능한 코드 집합 =====
 * referencechildren API는 부모 prefix를 넘기면 하위 유효 코드만 반환한다고 가정.
 * - generic 선택 시:  "<G>."                -> usage 후보
 * - usage   선택 시:  "<G>.<U>."            -> strength 후보
 * - strength선택 시:  "<G>.<U>.<S>."        -> type 후보
 */
const available = reactive({
  usage: new Set(),      // e.g., {'0','2','7','9'}
  strength: new Set(),   // e.g., {'A'}
  type: new Set(),       // e.g., {'P','D'}
})

const refCode = computed(() =>
  [selected.generic, selected.usage, selected.strength, selected.type].join('.')
)

/** ===== 이벤트 핸들러 ===== */
async function onPickGeneric() {
  // 상위 바뀌면 하위 리셋
  selected.usage = ''
  selected.strength = ''
  selected.type = ''
  available.strength = new Set()
  available.type = new Set()

  available.usage = new Set(await fetchChildren(`${selected.generic}.`))
  // 사용가능 usage 없으면 힌트
  hint.value = available.usage.size ? '' : '선택 가능한 Usage가 없습니다.'
  showDoc.value = false
  doc.value = null
}

async function onPickUsage() {
  selected.strength = ''
  selected.type = ''
  available.type = new Set()

  if (!selected.generic) return
  available.strength = new Set(await fetchChildren(`${selected.generic}.${selected.usage}.`))
  hint.value = available.strength.size ? '' : '선택 가능한 Strength가 없습니다.'
  showDoc.value = false
  doc.value = null
}

async function onPickStrength() {
  selected.type = ''
  if (!selected.generic || !selected.usage) return
  available.type = new Set(await fetchChildren(`${selected.generic}.${selected.usage}.${selected.strength}.`))
  hint.value = available.type.size ? '' : '선택 가능한 Control Type이 없습니다.'
  showDoc.value = false
  doc.value = null
}

async function onSearch() {
  hint.value = ''
  // 요구사항: 버튼은 항상 활성화. 미선택 시 버튼 아래 텍스트로 안내.
  if (!selected.generic) return void (hint.value = 'Generic을 선택해주세요.')
  if (!selected.usage) return void (hint.value = 'Usage를 선택해주세요.')
  if (!selected.strength) return void (hint.value = 'Strength를 선택해주세요.')
  if (!selected.type) return void (hint.value = 'Control Type을 선택해주세요.')

  // 예: "2.0.A.P"
  const referenceCode = refCode.value
  try {
    const res = await selectLibrary({ referenceCode })
    // API 구조에 맞춰 단건/배열 케이스 케어
    const row = Array.isArray(res?.data) ? res.data[0] : (res?.data || res)
    if (!row) {
      hint.value = '해당 코드의 문서가 없습니다.'
      showDoc.value = false
      doc.value = null
      return
    }
    doc.value = row
    showDoc.value = true
  } catch (e) {
    hint.value = '조회 중 오류가 발생했습니다.'
    showDoc.value = false
    doc.value = null
  }
}

/** ===== helpers ===== */
async function fetchChildren(referenceCode) {
  try {
    const res = await referencechildren({ referenceCode })
    // 응답이 ['0','2','7'] 또는 [{code:'0'}, ...] 등일 수 있으니 유연 처리
    const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
    const codes = list.map(v => (typeof v === 'string' ? v : v.code)).filter(Boolean)
    return codes
  } catch (e) {
    return []
  }
}
</script>

<style scoped>
.archive-tree {
  font-family: 'Noto Sans KR', sans-serif;
  padding: 24px;
  background: #fff;
  color: #111;
  line-height: 1.7;
  max-width: 1200px;
  margin: 0 auto;
}

.title {
  font-size: 1.625rem;
  font-weight: 700;
  margin-bottom: 12px;
}

.desc p {
  font-size: 0.95rem;
  margin-bottom: 12px;
}


.usage-guide-wrap {
  font-weight: 700;
  font-size: 1.25rem;
}

.usage-guide {
  margin-top: 12px;
  font-size: 0.95rem;
  line-height: 1.75;
}

.usage-guide ol {
  list-style: none;
  padding-left: 0;
  counter-reset: step;
}

.usage-guide li {
  margin-bottom: 18px;
}

.usage-guide strong {
  display: inline-block;
  font-weight: 700;
  color: #0f172a;
}

.usage-guide em {
  display: block;
  margin-top: 4px;
  color: #555;
  font-style: normal;
  font-size: 0.9rem;
}

/* 조회 영역 */
.search-fixed {
  /* position: sticky; */
  bottom: 0;
  background: #fff;
  padding: 16px 0;
  text-align: center;
  z-index: 10;
}

.btn-search {
  font-size: 1rem;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 8px;
  border: 1px solid #30308a;
  background: #30308a;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
}


/* ✅ 항상 hover/active만 유지, disabled 관련 제거 */
.btn-search:hover {
  background: #30308a;
}

.btn-search:active {
  transform: scale(0.97);
}

.hint-msg {
  margin-top: 8px;
  font-size: 0.9rem;
  color: #b91c1c;
  /* 붉은 톤 */
}

.hint { margin-top: 8px; color: #ef4444; }

.selector {
  max-width: 980px;
  margin: 40px auto;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
}

.group {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.group h3 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.group label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 0.95rem;
}


/* ===== 기본 타이포 ===== */
:where(*) {
  font-family: 'Noto Sans KR', sans-serif;
}

.title1 {
  font-size: 1.625rem;
  font-weight: 700;
}

p {
  font-size: 0.95rem;
}

/* ===== 레이아웃 ===== */
.control-sheet {
  max-width: 820px;
  margin: 24px auto;
  padding: 16px;
  background: #fff;
}

.title1 {
  text-align: center;
  margin: 28px 0 14px;
}

/* ===== 표(그리드) - 내부 보더 정리 버전 ===== */
.sheet-grid {
  border: 1px solid #888;
  /* 바깥 테두리 */
}

/* 행 기본: 보더는 '셀'에만 준다 (겹침 방지) */
.row {
  display: grid;
  grid-template-columns: 200px 1fr;
}

/* 모든 셀의 기본 보더는 위쪽만 */
.row>* {
  padding: 12px 14px;
  min-height: 54px;
  border-top: 1px solid #888;
  /* 행 사이 구분선 */
}

/* 첫 번째 행은 위쪽 선 제거 (바깥 테두리와 겹치지 않게) */
.sheet-grid>.row:first-child>* {
  border-top: none;
}

/* 각 행에서 첫 번째 셀을 제외한 나머지 셀에 왼쪽 선 */
.row>*:not(:first-child) {
  border-left: 1px solid #888;
  /* 열 구분선 */
}

/* 헤더 셀 꾸밈 */
.th {
  font-size: 0.9rem;
  font-weight: 700;
  background: #f7f7f7;
}

/* 첫 행: Ref + Name 나란히 (4열) */
.row--head {
  grid-template-columns: 200px 1fr 200px 1fr;
}

.row--head .ref-code,
.row--head .name {
  font-weight: 700;
}

/* 메타 정보 4쌍 (4열) */
.row--meta {
  grid-template-columns: 200px 1fr 200px 1fr;
}

/* ===== 반응형 ===== */
@media (max-width: 720px) {
  .row {
    grid-template-columns: 1fr;
  }

  /* 한 열로 쌓기 */
  .row>*:not(:first-child) {
    border-left: none;
  }

  /* 한 열일 땐 좌측선 제거 */
  .th {
    border-bottom: 1px solid #888;
  }

  /* 라벨과 값 사이 분리감 */
}


</style>
